import { memo } from 'react';

import { ThresholdBarTemplate } from './ThresholdBarTemplate.js';
import { THRESHOLD_BAR_THICKNESS } from './types/constants.js';

/**
 * @internal
 */
export interface ThresholdMarker {
  /** Unique identifier for the marker. */
  id: string;
  /** The unique color picked for this representation in HEX, RGB, Color Token or HSL. */
  color: string;
  /** Start position in pixels (not domain value). */
  y0: number;
  /** End position in pixels (not domain value). */
  y1: number;
  /** Formatted value to display for the marker. */
  value: string;
  /** Optional label for the marker. */
  label?: string;
}

/**
 * @internal
 */
export type LeftBorder = 'leftBorder';
/**
 * @internal
 */
export const LeftBorder: LeftBorder = 'leftBorder';
/**
 * @internal
 */
export type RightBorder = 'rightBorder';
/**
 * @internal
 */
export const RightBorder: RightBorder = 'rightBorder';
/**
 * @internal
 */
export type BothBorders = 'bothBorders';
/**
 * @internal
 */
export const BothBorders: BothBorders = 'bothBorders';

/**
 * @internal
 */
export type ThresholdBarProps = {
  /** Control which side the ThresholdArea border needs to be displayed. **/
  borderArea?: BothBorders | LeftBorder | RightBorder;
  /** Length in absolute pixels for the component.
   * If layout is vertical, this length is used for height.
   * Otherwise, it's used as width.
   */
  length: number;
  /** If layout should be vertical or horizontal */
  layout: 'vertical' | 'horizontal';
  /** Flips the component considering its current layout.
   * Used to allow the border to be in diferent positions
   * depending on the layout.
   */
  isFlipped?: boolean;
  /** Callback used when any of the thresholds markers are hovered, the id of the hovered item is passed as an argument. When 'undefined', none item is being hovered */
  onHover?: (id: string | undefined) => void;
  // displays both borders
  /** Markers information to be printed */
  markers: ThresholdMarker[];
};

const ThresholdBarInternal = (props: ThresholdBarProps) => {
  const { length, layout = 'vertical' } = props;

  return layout === 'horizontal' ? (
    /* This is needed to adapt the container to the rotated threshold bar */
    <div
      style={{ width: length, height: THRESHOLD_BAR_THICKNESS }}
      data-testid="threshold-bar-horizontal"
    >
      <ThresholdBarTemplate {...props} />
    </div>
  ) : (
    <div data-testid="threshold-bar-vertical">
      <ThresholdBarTemplate {...props} />
    </div>
  );
};

ThresholdBarInternal['displayName'] = 'ThresholdBarInternal';

/**
 * @internal
 */
export const ThresholdBar = memo(ThresholdBarInternal);

ThresholdBar['displayName'] = 'ThresholdBar';
